/**
 * TrustGate Console - Lifecycle Timeline
 * 
 * Vertical timeline showing state progression of a Bound Intent
 * Each node represents a governance event with expandable details
 */

import React, { useState } from 'react';
import { formatTimestamp } from '../../data/TrustGateDataService';
import { getStatusMeta } from '../../shared/status';
import type { TrustGateEvent } from '../../ecs/types';

interface LifecycleTimelineProps {
  event: TrustGateEvent;
  onViewReceipt: (type: 'binding' | 'execution') => void;
}

interface TimelineNode {
  id: string;
  icon: string;
  label: string;
  time?: string;
  status: 'completed' | 'current' | 'pending';
  color: string;
  details?: Record<string, string>;
  hasReceipt?: boolean;
  receiptType?: 'binding' | 'execution';
}

export const LifecycleTimeline: React.FC<LifecycleTimelineProps> = ({
  event,
  onViewReceipt,
}) => {
  const [expandedNode, setExpandedNode] = useState<string | null>(null);

  const buildTimeline = (): TimelineNode[] => {
    const nodes: TimelineNode[] = [];
    const status = event.status.toUpperCase();

    // 1. Intent Drafted (always implied)
    const draftMeta = getStatusMeta('draft');
    nodes.push({
      id: 'draft',
      icon: draftMeta.icon,
      label: draftMeta.label,
      time: formatTimestamp(event.timestamp),
      status: 'completed',
      color: `text-[${draftMeta.color}]`,
      details: {
        'Requested By': (event as any).requestedByEmail || 'Unknown',
        'Entity ID': event.entityId,
      },
    });

    // 2. Intent Submitted (when created)
    const submittedMeta = getStatusMeta('submitted');
    nodes.push({
      id: 'submitted',
      icon: submittedMeta.icon,
      label: submittedMeta.label,
      time: formatTimestamp(event.timestamp),
      status: 'completed',
      color: `text-[${submittedMeta.color}]`,
      details: {
        'Submission Time': formatTimestamp(event.timestamp),
        'Intent Type': event.entityKind || 'action_request',
      },
    });

    // 3. Awaiting Binding (if pending or beyond)
    if (status === 'PENDING' || status === 'APPROVED' || status === 'ALLOW' || status === 'EXECUTED') {
      const awaitingMeta = getStatusMeta('awaiting_binding');
      nodes.push({
        id: 'awaiting',
        icon: awaitingMeta.icon,
        label: awaitingMeta.label,
        time: status === 'PENDING' ? 'In Progress' : formatTimestamp(event.timestamp),
        status: status === 'PENDING' ? 'current' : 'completed',
        color: `text-[${awaitingMeta.color}]`,
        details: {
          'Policy Evaluation': 'Governance review',
          'Status': status === 'PENDING' ? 'Evaluating' : 'Completed',
        },
      });
    }

    // 4. Intent Bound (if approved/allow)
    if (status === 'APPROVED' || status === 'ALLOW' || status === 'EXECUTED') {
      const boundMeta = getStatusMeta('bound');
      nodes.push({
        id: 'bound',
        icon: boundMeta.icon,
        label: boundMeta.label,
        time: formatTimestamp((event as any).updatedAt || event.timestamp),
        status: status === 'EXECUTED' ? 'completed' : 'current',
        color: `text-[${boundMeta.color}]`,
        details: {
          'Authority': (event as any).authority || 'trustgate_admin',
          'Policy Version': (event as any).policyVersion || 'v3.2.1',
          'Binding Receipt': event.proofId || 'Available',
        },
        hasReceipt: !!event.proofId,
        receiptType: 'binding',
      });
    }

    // 5. Bound Intent Executed (if executed)
    if (status === 'EXECUTED') {
      const executedMeta = getStatusMeta('executed');
      nodes.push({
        id: 'executed',
        icon: executedMeta.icon,
        label: executedMeta.label,
        time: formatTimestamp((event as any).executionTimestamp || (event as any).updatedAt),
        status: 'current',
        color: `text-[${executedMeta.color}]`,
        details: {
          'Execution Time': formatTimestamp((event as any).executionTimestamp || (event as any).updatedAt),
          'Execution Receipt': 'Available',
        },
        hasReceipt: true,
        receiptType: 'execution',
      });
    }

    // Alternative: Binding Denied
    if (status === 'DENIED' || status === 'DENY') {
      const deniedMeta = getStatusMeta('denied');
      nodes.push({
        id: 'denied',
        icon: deniedMeta.icon,
        label: deniedMeta.label,
        time: formatTimestamp((event as any).updatedAt || event.timestamp),
        status: 'current',
        color: `text-[${deniedMeta.color}]`,
        details: {
          'Denial Time': formatTimestamp((event as any).updatedAt || event.timestamp),
          'Reason': event.reasonCode || 'Policy violation',
          'Authority': (event as any).authority || 'System',
        },
      });
    }

    return nodes;
  };

  const timeline = buildTimeline();

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold text-[#f8fafc] mb-6">Lifecycle Timeline</h3>
      
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-white/10"></div>

        {/* Timeline nodes */}
        <div className="space-y-6">
          {timeline.map((node) => (
            <div key={node.id} className="relative">
              {/* Node circle */}
              <div 
                className={`absolute left-3 w-6 h-6 rounded-full border-2 flex items-center justify-center
                  ${node.status === 'completed' ? 'bg-white/10 border-white/30' : ''}
                  ${node.status === 'current' ? 'bg-[#00d4aa]/20 border-[#00d4aa] shadow-lg shadow-[#00d4aa]/50' : ''}
                  ${node.status === 'pending' ? 'bg-transparent border-white/10' : ''}
                `}
              >
                {node.status === 'current' && (
                  <div className="w-2 h-2 rounded-full bg-[#00d4aa] animate-pulse"></div>
                )}
              </div>

              {/* Node content */}
              <div className="ml-16">
                <button
                  onClick={() => setExpandedNode(expandedNode === node.id ? null : node.id)}
                  className="w-full text-left"
                >
                  <div className={`text-base font-semibold ${node.color} flex items-center gap-2`}>
                    <span>{node.icon}</span>
                    <span>{node.label}</span>
                  </div>
                  <div className="text-sm text-[#cbd5e1] mt-1">
                    {node.time}
                  </div>
                </button>

                {/* Expanded details */}
                {expandedNode === node.id && node.details && (
                  <div className="mt-3 p-3 bg-white/5 border border-white/10 rounded-lg space-y-2">
                    {Object.entries(node.details).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-[#cbd5e1]">{key}:</span>
                        <span className="text-[#f8fafc] font-mono text-xs">{value}</span>
                      </div>
                    ))}
                    
                    {node.hasReceipt && node.receiptType && (
                      <button
                        onClick={() => onViewReceipt(node.receiptType!)}
                        className="w-full mt-2 px-3 py-1.5 bg-[#00d4aa] hover:bg-[#00a8cc] text-white text-sm rounded transition-colors"
                      >
                        View {node.receiptType === 'binding' ? 'Binding' : 'Execution'} Receipt
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Truth Statement */}
      <div className="mt-8 p-4 bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-lg">
        <div className="text-sm text-[#00d4aa] font-semibold mb-1">
          🔐 System Truth = Receipts
        </div>
        <div className="text-xs text-[#cbd5e1]">
          Each state transition is backed by cryptographic receipts that prove authority occurred.
        </div>
      </div>
    </div>
  );
};
